import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { LeadSourcesService } from "../../../services/user/leadsources.services"
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import DeleteModelPopupComponent from "../common_model_popup/deletemodelpopup.component";
import { handleCopy, handleExcel, handlePDF, handlePrint } from "../../../utils/filedownload";

const LeadSourcesComponent = () => {
    const navigate = useNavigate();
    const selectedStore = useSelector(state => state?.user?.selectedStoreId);
    const [searchText, setSearchText] = useState("");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [leadSourceToDelete, setLeadSourceToDelete] = useState(null);
    const rowsPerPage = 5;

    // Fetch data
    useEffect(() => {
        const fetchLeadSourceData = async () => {
            try {
                setLoading(true);
                const response = await LeadSourcesService.getAllLeadSources(selectedStore);
                setData(response);
            } catch (error) {
                handleNotify("Error fetching lead sources", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                setData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchLeadSourceData();
    }, [selectedStore]);

    // Handle pagination
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentData = data.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(data.length / rowsPerPage);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    // Handle Edit
    const handleEditClick = (item) => {
        navigate('/leadsources/edit', { state: { leadSourcesId: item?.leadSourcesId } });
    };

    // Handle Delete
    const handleDeleteClick = (leadSource) => {
        setLeadSourceToDelete(leadSource);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (leadSourceToDelete) {
            try {
                await LeadSourcesService.deleteLeadSource(leadSourceToDelete.leadSourcesId);
                setData(prevData => prevData.filter(leadSource => leadSource.leadSourcesId !== leadSourceToDelete.leadSourcesId));
                handleNotify("Lead source deleted successfully!", TOASTER_TYPE.SUCCESS, TOASTER_POSITION.TOP_RIGHT);
                setShowModal(false);
            } catch (error) {
                handleNotify(error.message || 'Error while deleting lead source.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
            }
        }
    };

    const handleAddNewClick = () => {
        navigate('/leadsources/create');
    };


    const filteredData = currentData.filter(row =>
        row.leadSourceName.toLowerCase().includes(searchText.toLowerCase()) ||
        (row.isActive ? "Active" : "Inactive").toLowerCase().includes(searchText.toLowerCase())
    );

    // Columns for DataTable
    const columns = [
        {
            name: "Name",
            selector: (row) => row.leadSourceName,
            sortable: true,
            width: '60%',
        },
        {
            name: "Active?",
            width: '15%',
            selector: row => row.isActive,
            sortable: true,
            cell: row => (
                <button className="btn btn-light radius-30 px-4 text-success">
                    {row.isActive === true ? "Active" : "Inactive"}
                </button>
            ),
        },
        {
            name: "Actions",
            button: true,
            width: "25%",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-primary" onClick={() => handleEditClick(row)}>
                        Edit
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDeleteClick(row)}>
                        Delete
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="page-wrapper m-0">
                <div className="page-content">
                    <div className="page-breadcrumb d-flex align-items-center mb-3">
                        <div className="breadcrumb-title pe-3 text-primary fw-bold fs-4">
                            Lead Sources
                        </div>
                        <div className="ms-auto">
                            <button className="btn btn-primary" onClick={handleAddNewClick}>
                                New +
                            </button>
                        </div>
                    </div>

                    <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                        <div className="card-body">
                            <div className="d-flex flex-column flex-md-row mb-3">
                                <div className="btn-group mb-3 mb-md-0 me-2">
                                    <button className="btn btn-outline-secondary" onClick={() => handleCopy(filteredData)}>Copy</button>
                                    <button className="btn btn-outline-secondary" onClick={() => handleExcel(filteredData)}>Excel</button>
                                    <button className="btn btn-outline-secondary" onClick={() => handlePDF(filteredData)}>PDF</button>
                                    <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                                </div>

                                <div className="d-flex ms-auto align-items-center">
                                    <div className="me-2">
                                        <span className="fw-bold">Search:</span>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control w-100 w-md-auto"
                                        placeholder="Search 🔍..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="table-responsive">
                                <DataTable
                                    columns={columns}
                                    data={filteredData}
                                    fixedHeader
                                    responsive
                                    highlightOnHover
                                    sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                />
                            </div>
                            {showModal && (
                                <DeleteModelPopupComponent
                                    show={showModal}
                                    onClose={() => setShowModal(false)}
                                    onConfirm={handleDeleteConfirm}
                                    title="Confirm"
                                    confirmText="Delete"
                                    Message="Are you sure you want to delete this lead source?"
                                />
                            )}

                            {/* Pagination Controls */}
                            <div className="d-flex mt-4 justify-content-end">
                                <div className="btn-group">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                    >
                                        Prev
                                    </button>
                                    {Array.from({ length: totalPages }, (_, index) => (
                                        <button
                                            key={index}
                                            className={`btn ${currentPage === index + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => handlePageChange(index + 1)}
                                        >
                                            {index + 1}
                                        </button>
                                    ))}
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LeadSourcesComponent;
